/**
 * Spoken UI clips (greetings, intros) in the site language.
 *
 * English keeps the existing behaviour (pre-recorded WAVs / live TTS).
 * Other languages: the backend translates the clip text once, generates the WAV once
 * with the TTS service (in the user's voice when given) and stores both — so every
 * language gets its own WAV that is reused for all users:
 *     POST /api/i18n/voice-clip  {text, lang, voice, vars}  ->  {audio_path}
 * The browser also remembers the URL, so a clip is fetched once per device.
 *
 * Pre-recorded English intro WAVs: add the words spoken in each file to CLIP_TEXTS
 * below; until a text is filled in, that clip stays English in every language.
 */
import axios from "axios";
import { getCurrentLang } from "./googleTranslate";

const API_BASE = (process.env.REACT_APP_SERVER_ENDPOINT || "").replace(/\/+$/, "");
const ENDPOINT = `${API_BASE}/api/i18n/voice-clip`;
const STORE_KEY = "voice_clip_cache_v1";

/** English pre-recorded intro WAV -> the exact English words spoken in it. */
export const CLIP_TEXTS: Record<string, string> = {
  // Onboarding step 1 (onboardingPage.tsx)
  "https://tts.ocesha.ai/media/2a8e87c0-dcc2-49f8-aa32-30c2cb351d16.wav": "I am the AI Concierge, the digital twin of this platform. I've been trained to help you get started, answer your questions, and guide you every step of the way. Think of me as your personal guide — here whenever you need me.",
  // Onboarding step 2 (new_course_1.tsx)
  "https://tts.ocesha.ai/media/77c2036c-93d8-4ede-baed-7dd5f23d5ec9.wav": "Now let’s add content to build your course. The more content you add, the better OceSha can understand your expertise and create a course that sounds like you. You can upload: Documents and presentations, Websites and articles, Audio or video (podcasts, recordings, etc.), and Notes, scripts, or other text. We'll turn this into a structured, high-quality course.",
  // Onboarding step 3 (new_course_2.tsx)
  "https://tts.ocesha.ai/media/48845f03-61a7-4c5e-890f-7abb5a2e1a84.wav": "We're analyzing your content to understand your expertise, experience, and teaching style. This helps OceSha create courses that reflect your knowledge and prepares your AI to teach and answer student questions.",
  // Onboarding step 4, final (new_course_4.tsx)
  "https://tts.ocesha.ai/media/ebe67d5c-4563-42e8-a51b-17f3a0aba257.wav": "You're almost done! Choose the tone and style you want OceSha to use for your courses and content. We'll apply it across: Courses & lessons, AI teaching, Blogs & social posts, and Marketing content. You can change this anytime.",
};

/** Greeting used before the intro clips ("Hi Saravanan") */
export const GREETING_TEXT = "Hi {0}";

const readCache = (): Record<string, string> => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || "{}") || {}; } catch { return {}; }
};
const writeCache = (key: string, url: string) => {
  try {
    const c = readCache();
    c[key] = url;
    const keys = Object.keys(c);
    if (keys.length > 200) keys.slice(0, keys.length - 200).forEach((k) => delete c[k]);
    localStorage.setItem(STORE_KEY, JSON.stringify(c));
  } catch { /* ignore */ }
};

const normVoice = (voice?: string) => (voice || "default").replace(/\.wav$/i, "");

/**
 * Audio URL for `text` (English, may contain {0}…) spoken in the site language.
 * Returns null when it can't be generated — callers then fall back to English.
 */
export const getVoiceClipUrl = async (
  text: string,
  opts: { voice?: string; vars?: string[]; lang?: string } = {}
): Promise<string | null> => {
  const lang = opts.lang || getCurrentLang() || "en";
  const voice = normVoice(opts.voice);
  const vars = (opts.vars || []).map((v) => String(v ?? ""));
  const cacheKey = `${lang}|${voice}|${text}|${vars.join("\u0001")}`;
  const cached = readCache()[cacheKey];
  if (cached) return cached;
  try {
    const res = await axios.post(ENDPOINT, { text, lang, voice, vars });
    const url = res?.data?.audio_path || null;
    if (url) writeCache(cacheKey, url);
    return url;
  } catch (e) {
    console.warn("voice clip failed", e);
    return null;
  }
};

/** "Hi {name}" in the site language (null -> caller keeps its English request). */
export const getGreetingUrl = (name: string, voice?: string) =>
  (getCurrentLang() || "en") === "en"
    ? Promise.resolve(null)
    : getVoiceClipUrl(GREETING_TEXT, { voice, vars: [name || ""] });

/** A pre-recorded English intro WAV -> the same words in the site language, else the English file. */
export const localizedClipUrl = async (englishUrl: string, voice?: string): Promise<string> => {
  const text = CLIP_TEXTS[englishUrl];
  if (!text || (getCurrentLang() || "en") === "en") return englishUrl;
  return (await getVoiceClipUrl(text, { voice })) || englishUrl;
};
